import * as Yup from "yup";

import { TextManager } from "Components/TextContext";
import { UserAuthorizationLevel } from "Store/advice/interfaces";
import { AdviceType } from "Store/advicePDF/interfaces";
import {
  CollectiveAdvice,
  CoverageNeed,
  NeedName,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import { getPaymentRestriction } from "Store/akaAdvice/interfaces/getters";
import { TextLanguage } from "Store/texts/interfaces";
import { isEmpty } from "Utils/functions";
import { OneTimePaymentAdvisorChoice } from "../ProductPrice/AdviceCards/interfaces";
import { mustVerifyAdviceChoices } from "../ProductPriceAka/AkaAdviceMessages/adviceDiffMessagesUtils";
import { isWithinValidOtpRange } from "./AkaAdviceCards/DisabilityAdviceCard/Forms/DisabilityOneTimePayment/utils";
import { AkaAdvicePriceFormValues } from "./AkaAdviceCards/interfaces";
import {
  getPercentOfPDFDocumentCommentSpaceUsed,
  isNeedSelectionEqualToAdvice,
} from "./utils";

export const validationSchema = (
  isAdvisor: boolean,
  doubleYearlyIncome: number,
  advice: CollectiveAdvice,
  adviceType: AdviceType,
  adviceText: any,
  lang: TextLanguage,
): Yup.ObjectSchema<AkaAdvicePriceFormValues> => {
  const { getText } = TextManager;

  const minCustomerDisabilityCapitalPayment = getPaymentRestriction(
    advice.needs[NeedName.DISABILITY],
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  ).minPayouts[UserAuthorizationLevel.CUSTOMER];
  return Yup.object({
    deathCheckbox: Yup.boolean().required(),
    akaGroupLifeDeathCheckbox: Yup.boolean().required(),
    akaGroupLifeDeathGLevel: Yup.string().when(["akaGroupLifeDeathCheckbox"], {
      is: true,
      then: (schema) => schema.required(),
    }),
    akaGroupLifeDeathCoInsuredCheckbox: Yup.boolean(),
    akaGroupLifeDeathCoInsuredGLevel: Yup.string(),
    deathInsuredSumCheckbox: Yup.boolean().required(),
    deathInsuredSumSlider: Yup.number(),

    disabilityCheckbox: Yup.boolean().required(),
    incapacityOfWorkCheckbox: Yup.boolean().required(),
    incapacityOfWorkGLevel: Yup.string().when(["incapacityOfWorkCheckbox"], {
      is: true,
      then: (schema) => schema.required(),
    }),
    timeLimitedDisabilityInterestCheckbox: Yup.boolean().required(),
    timeLimitedDisabilityInterestGLevel: Yup.string().when(
      ["timeLimitedDisabilityInterestCheckbox"],
      { is: true, then: (schema) => schema.required() },
    ),
    incapacityOfWorkCoInsuredCheckbox: Yup.boolean(),
    incapacityOfWorkCoInsuredGLevel: Yup.string(),
    timeLimitedDisabilityInterestCoInsuredCheckbox: Yup.boolean(),
    timeLimitedDisabilityInterestCoInsuredGLevel: Yup.string(),
    disabilityInsuredSumCheckbox: Yup.boolean().required(),
    disabilityInsuredSumSlider: Yup.number(),
    disabilityOneTimePaymentCheckbox: Yup.boolean().required(),
    disabilityOneTimePaymentValue: Yup.number().when(
      "disabilityOneTimePaymentCheckbox",
      {
        is: (disabilityOneTimePaymentCheckbox: boolean) =>
          !isAdvisor && !!disabilityOneTimePaymentCheckbox,
        then: (schema) =>
          schema.min(
            minCustomerDisabilityCapitalPayment,
            adviceText.adviceCards.disability.disabilityOneTimePayment.options
              .required[lang],
          ),
      },
    ),
    disabilityOneTimePaymentChangedWarning: Yup.boolean(),

    criticalIllnessCheckbox: Yup.boolean().required(),
    akaCriticalIllnessCoverageAmountCheckbox: Yup.boolean().required(),
    akaCriticalIllnessCoverageAmountGLevel: Yup.string().when(
      ["akaCriticalIllnessCoverageAmountCheckbox"],
      { is: true, then: (schema) => schema.required() },
    ),
    akaCriticalIllnessCoverageAmountCoInsuredCheckbox: Yup.boolean(),
    akaCriticalIllnessCoverageAmountCoInsuredGLevel: Yup.string(),
    criticalIllnessInsuredSumCheckbox: Yup.boolean().required(),
    criticalIllnessInsuredSumSlider: Yup.number(),

    adviceDiffConsent: Yup.boolean().test({
      test: (value, context) =>
        mustVerifyAdviceChoices(
          advice,
          context.parent as Partial<AkaAdvicePriceFormValues>,
          adviceType,
          isAdvisor,
        )
          ? value === true
          : true,
      message: getText("advicepage.advicediff.consentrequired"),
    }),
  })
    .concat(
      advisorValidationSchema(
        isAdvisor,
        doubleYearlyIncome,
        advice.needs[NeedName.DISABILITY],
        advice,
        adviceType,
      ),
    )
    .defined();
};

type AdvisorValues =
  | "disabilityPaymentAfter3Months"
  | "disabilityOneTimePaymentAdvisorChoice"
  | "disabilityOneTimePaymentAdvisorValue"
  | "deathAdviceComment"
  | "disabilityAdviceComment"
  | "criticalIllnessAdviceComment";

const advisorValidationSchema = (
  isAdvisor: boolean,
  doubleYearlyIncome: number,
  need: CoverageNeed,
  advice: CollectiveAdvice,
  adviceType: AdviceType,
): Yup.ObjectSchema<Pick<AkaAdvicePriceFormValues, AdvisorValues>> => {
  const { getText } = TextManager;
  return Yup.object({
    disabilityPaymentAfter3Months: Yup.boolean().when(
      ["disabilityCheckedToPurchase"],
      {
        is: (disabilityCheckbox: boolean) => isAdvisor && disabilityCheckbox,
        then: (schema) =>
          schema.required(
            getText("adviceform.validation.disabilitypaymentafter3months"),
          ),
      },
    ),
    disabilityOneTimePaymentAdvisorChoice:
      Yup.mixed<OneTimePaymentAdvisorChoice>()
        .required()
        .when("disabilityCheckbox", {
          is: (disabilityCheckbox: boolean) => isAdvisor && disabilityCheckbox,
          then: (schema) =>
            schema.required(
              getText(
                "adviceform.validation.disabilityonetimepaymentadvisorchoice",
              ),
            ),
        }),
    disabilityOneTimePaymentAdvisorValue: Yup.number().when(
      ["disabilityCheckbox", "disabilityOneTimePaymentAdvisorChoice"],
      {
        is: (
          disabilityCheckbox: boolean,
          disabilityOneTimePaymentAdvisorChoice: OneTimePaymentAdvisorChoice,
        ) =>
          isAdvisor &&
          disabilityCheckbox &&
          disabilityOneTimePaymentAdvisorChoice ===
            OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT,
        then: (schema) =>
          schema
            .required("Engangsutbetaling må oppgis")
            .test(
              "disabilityOneTimePaymentAdvisorValue",
              "Engangsutbetaling er ugyldig - minimum 400.000, maks 2x årslønn, maks 4 mill.",
              (value: number) =>
                isWithinValidOtpRange(doubleYearlyIncome, value, need),
            ),
      },
    ),
    deathAdviceComment: adviceCommentValidation(
      NeedName.LIFE,
      advice,
      isAdvisor,
      adviceType,
    ),
    disabilityAdviceComment: adviceCommentValidation(
      NeedName.DISABILITY,
      advice,
      isAdvisor,
      adviceType,
    ),
    criticalIllnessAdviceComment: adviceCommentValidation(
      NeedName.CRITICAL_ILLNESS,
      advice,
      isAdvisor,
      adviceType,
    ),
  }).defined();
};

const adviceCommentValidation = (
  needName: NeedName,
  advice: CollectiveAdvice,
  isAdvisor: boolean,
  adviceType: AdviceType,
): Yup.StringSchema<string | undefined> => {
  const { getText } = TextManager;
  return Yup.string()
    .test({
      name: "Required when advisor selection doesn't follow advice",
      test: (value, context) =>
        !isAdvisor ||
        isNeedSelectionEqualToAdvice(
          needName,
          context.parent as Partial<AkaAdvicePriceFormValues>,
          advice,
          isAdvisor,
        ) ||
        !isEmpty(value),
      message: getText("adviceform.validation.advisorcommentrequired"),
    })
    .when([], {
      is: () => adviceType === AdviceType.INDIVIDUAL,
      then: (schema) =>
        schema.test({
          name: "Total max length across needs",
          test: (value, context) =>
            isEmpty(value) ||
            getPercentOfPDFDocumentCommentSpaceUsed(
              context.parent as Partial<AkaAdvicePriceFormValues>,
            ) <= 100,
          message: getText("adviceform.validation.advisorcommentmaxlength"),
        }),
    });
};
